import { useCallback, useEffect, useMemo, useRef } from 'react';

import isEqual from 'lodash/isEqual';

import { useGetCompetitionCollections, useGetFantasyCollectionValidity } from '@pkg/hooks';

import { CollectionChainType } from '@app/client';
import { useFantasyCollectionValidityStore, useFantasyLineupStore } from '@app/store/zustand';

export type ValiditySlugs = { slug: string; chain: CollectionChainType };
export const useGetUpdateCollectionsData = () => {
    const lineupItems = useFantasyLineupStore((state) => state.lineupItems);
    const collectionsStatuses = useFantasyCollectionValidityStore((state) => state.collectionsStatuses);
    const collectionsValidity = useFantasyCollectionValidityStore((state) => state.collectionsValidity);

    const getValidity = useGetFantasyCollectionValidity();
    const getStatuses = useGetCompetitionCollections();

    const validitySlugs = useMemo(
        () =>
            lineupItems.filter((item) => {
                const itemValidity = collectionsValidity[item.slug];
                if (!itemValidity) {
                    return item;
                }
            }),
        [collectionsValidity, lineupItems],
    );
    const statusSlugs = useMemo(
        () =>
            lineupItems
                .filter((item) => {
                    const itemStatus = collectionsStatuses[item.slug];
                    if (!itemStatus) {
                        return item;
                    }
                })
                .map((item) => item.slug),
        [collectionsStatuses, lineupItems],
    );

    const prevValiditySlugs = useRef(validitySlugs);
    const prevStatusSlugs = useRef(statusSlugs);

    const handleValiditySlugs = useCallback(
        (slugs: ValiditySlugs[], chain: CollectionChainType) => {
            const collectionSlugs = slugs
                .filter((s) => s?.chain === chain)
                ?.map((s) => s?.slug)
                .slice(0, 5);

            if (collectionSlugs?.length) {
                void getValidity({
                    collectionSlugs,
                    chain,
                    ignoreSignal: true,
                    updateStore: true,
                });
            }
        },
        [getValidity],
    );

    useEffect(() => {
        if (statusSlugs?.length && !isEqual(prevStatusSlugs.current, statusSlugs)) {
            // TODO check multi-chain statuses
            if (statusSlugs.length) {
                void getStatuses(statusSlugs);
            }

            prevStatusSlugs.current = statusSlugs;
        }
    }, [getStatuses, statusSlugs]);

    useEffect(() => {
        if (validitySlugs?.length && !isEqual(prevValiditySlugs.current, validitySlugs)) {
            handleValiditySlugs(validitySlugs, CollectionChainType.Ethereum);
            handleValiditySlugs(validitySlugs, CollectionChainType.Aptos);
            handleValiditySlugs(validitySlugs, CollectionChainType.Polygon);

            prevValiditySlugs.current = validitySlugs;
        }
    }, [handleValiditySlugs, validitySlugs]);
};
